// What each page's ? says, in one place (Craig, 20 Oct 2026: "page-context-aware Help … apply that to all
// pages"). Each is { title, lines }: a few numbered sentences about what the page is for and how to use it. `wideLines`
// replaces them on a wide screen where the sidebar changes what's true (Home's).
// Keep these true to the page: when a page changes, change its lines here.
export const HELP = {
  home: {
    title: "How to use Nudge",
    lines: [
      "Each tile on this page is a reason to open Nudge. Pick the one that matches what you need right now.",
      "Dreams, Fears and Key Dates help you decide what you want and what's coming up.",
      "Today is your plan for the day, and Radar shows what needs your attention.",
      "Goals and Tasks hold what you'll do to get what you want.",
      "If you're stuck or don't feel like starting, Motivation explains why and what helps.",
      "Mindset, Habits, Gratitude and Temptations hold what you can choose from each day in Today's Reminders, on the Today page.",
      "The bar at the bottom takes you to Today, Radar, Goals, Tasks and Chat from any page.",
      "The buttons at the top are on most pages. + lets you add anything: a dream, fear, goal, key date, task, mindset reminder, habit, gratitude reminder or temptation.",
      "On every other page, the arrow at the top left brings you back here.",
      "The magnifying glass searches your tasks and goals, the bell shows your nudges, and the gear opens Settings.",
    ],
    // On a wide screen the sidebar holds the bottom bar's pages and the top bar's buttons, and there's no ◀.
    wideLines: [
      "Each tile on this page is a reason to open Nudge. Pick the one that matches what you need right now.",
      "Dreams, Fears and Key Dates help you decide what you want and what's coming up.",
      "Today is your plan for the day, and Radar shows what needs your attention.",
      "Goals and Tasks hold what you'll do to get what you want.",
      "If you're stuck or don't feel like starting, Motivation explains why and what helps.",
      "Mindset, Habits, Gratitude and Temptations hold what you can choose from each day in Today's Reminders, on the Today page.",
      "The list on the left takes you to any page, from any page.",
      "Add new, at the top of the list, lets you add anything: a dream, fear, goal, key date, task, mindset reminder, habit, gratitude reminder or temptation.",
      "Search looks through your tasks and goals. Beside the Nudge logo, the bell shows your nudges and the gear opens Settings.",
    ],
  },
  chat: {
    title: "How to use Chat",
    lines: [
      "Chat is where you talk things through with Nudge whenever you feel stuck, overwhelmed or unmotivated.",
      "On a new chat, the buttons above the message box start a particular kind of conversation, like Update My Plan, Check In or Motivate Me. They fold away once you send something. Tap ⚡ beside the message box to see them again.",
      "Be honest about how you feel. The more you say, the better the help.",
      "Nudge remembers what you've said in this chat. Tap the circular arrow at the top to start a new one.",
      "When Nudge sets a time to check in with you, it shows on the Today page.",
    ],
  },
  learn: {
    title: "How to use Learn",
    lines: [
      "If you're new to this, start with How motivation works.",
      "Why we put things off lists the common reasons. Each one links to the strategies that help with it.",
      "If you're stuck right now, I'm stuck right now asks how you feel and suggests something to try.",
      "On any idea, Talk it through opens Chat to work through it with you.",
    ],
  },
  today: {
    title: "How to use Today",
    lines: [
      "Write today's win at the top: the one thing that would make today a success.",
      "Today's plan is split into Morning, Afternoon and Evening. The part of the day it is now opens on its own.",
      "Not sure what to put in your day? Tap Radar, under the Today's Plan heading, to see what's overdue, coming up and most important. On Radar, tap Add to today on a task to put it in your day.",
      "Tap + Add task in a part of the day to put a task there. In that sheet, Help me choose from my goals helps you pick one.",
      "Check in through the day. Each check-in is a moment to look at your plan and get back on track.",
      "Tap Today's Inspiration, under the check-in, to open it. It holds Motivation Moment and three buttons that start a chat. It stays open or closed the way you leave it.",
      "Today's Reminders, under it, shows the mindset reminders, habits, gratitude and temptations you chose for today. Tap the card to choose them. You add your own on their pages, from Home. Tap its heading to fold it away. It stays folded until tomorrow unless you open it again.",
      "Motivation Moment shows an idea that helps you get started. It changes in the morning, afternoon and evening. Tap it to read more.",
    ],
  },
  radar: {
    title: "How to use Radar",
    lines: [
      "Radar shows what deserves your attention today, so you don't have to go looking for it.",
      "Each group looks at your plan a different way: tasks that are overdue or due today, key dates coming up, tasks due soon, goals that are both urgent and important, and your most important goals.",
      "Radar also lists your habits, dreams and fears, so they stay in front of you.",
      "The Nudge card at the top shows your latest nudge. Tap Nudge to read it.",
      "Snooze for… puts the nudge away and stops new ones for the time you choose. Dismiss puts it away, and new nudges keep coming as usual. Next puts it away and shows the next one, when another is ready.",
      "Drag a group by its ⠿ to move it. Radar keeps the order you choose.",
      "Key Dates Coming Up shows each key date you're getting ready for, with how many days are left and how many tasks you still have to do. Tap a date to see its tasks and add them to today.",
      "A key date's task that is overdue, due today or due soon also shows in that group. It's counted once in the number at the top.",
      "Tap Add to today on a task to put it in the part of the day it is now, on the Today page. Tap the circle beside a task when it's done.",
      "Tap a goal's name to open the goal. Tap the rest of its bar to see its tasks.",
      "Tap a habit, dream or fear to change it.",
      "Tap + Add a task under a dream or fear to add a task for it. Once it has tasks, it says how many. Tap those words to see the tasks and add them to today.",
      "Tap a group's name to open it or close it. Groups start closed, and each one stays the way you leave it.",
      "Tap the sliders at the top to choose which groups show and how far ahead Tasks Due Soon looks.",
    ],
  },
  goals: {
    title: "How to use Goals",
    lines: [
      "This page holds your goals, with the tasks for each goal folded under it.",
      "Use the switch at the top to see your top goals, your goals by importance, by urgency or by preference, or on a calendar by deadline.",
      "Under the words at the top, tap All categories and choose a category to see only the goals in it. The tasks without a goal are shown by their own category. Choose All categories to see everything again.",
      "When you drag a goal with a category chosen, it moves to just above or below the goal you drop it on, among all your goals.",
      "Top lists the goals near the top of both Importance and Urgency, the same as Urgent and Important Goals on Radar. You can't drag in Top. Move a goal on Importance or Urgency to change it.",
      "Importance, Urgency and Preference are three orders you set yourself. Drag a goal by ⠿ to move it. The new order is saved as soon as you let go.",
      "Preference is the order you'd work on your goals in if it were only up to how you feel. When a goal sits much lower here than on Importance, it may be one you're putting off.",
      "It's easy to push an important goal down because the work is hard. Try to rank it where it belongs.",
      "Tap a goal's name to open it. Tap the rest of its bar to show or hide its tasks.",
      "Tap Add to today on a task to put it in the part of the day it is now, on the Today page.",
      "Radar shows the goals that are near the top of Importance and Urgency.",
    ],
  },
  tasks: {
    title: "How to use Tasks",
    lines: [
      "This page holds every task you haven't done.",
      "Use the switch at the top to choose how they're listed. All is A to Z, Deadlines puts the soonest first, Category groups them, and Oldest shows the ones that have waited a week or more.",
      "Tap the circle to mark a task done. You'll be asked to confirm first. Tap the task to change its deadline, goal and more.",
      "A task can be for a goal, a key date, a dream or a fear. Tap the task to choose which.",
      "Under a task you'll see its goal, and the key date, dream or fear it's for. The Dreamboard, Fears and Radar also show each dream's and fear's tasks under it.",
      "Tap Add to today on a task to put it in the part of the day it is now, on the Today page.",
      "Tasks you've done are under Completed at the bottom. Tap one to change it, or to mark it not done.",
    ],
  },
  dreams: {
    title: "How to use your Dreamboard",
    lines: [
      "Your Dreamboard holds the things you want to have or experience. A photo of each makes it feel real.",
      "Seeing what you want often makes it easier to do the hard things that get you there.",
      "Drag a dream by ⠿ to put the ones that matter most first.",
      "Tap Names to see only the name of each dream, which makes them easier to drag into order. Tap Photos to see them again.",
      "Tap a dream to change its words or photo, or to delete it.",
      "Tap + Add a task under a dream to add a task that gets you closer to it. Once it has tasks, it says how many. Tap those words to see the tasks and add them to today.",
    ],
  },
  fears: {
    title: "How to use Fears",
    lines: [
      "Fears are what you're afraid will happen if you keep putting things off.",
      "Writing them down makes the cost of waiting feel real today, not someday.",
      "Fears are grouped by your categories. Tap a category to fold it away.",
      "Drag a fear by ⠿ to move it within its category.",
      "Tap a fear to see the goals that will keep it from happening. The pencil changes the fear and its goals.",
      "Tap + Add a task under a fear to add a task that keeps it from happening. Once it has tasks, it says how many. Tap those words to see the tasks and add them to today.",
    ],
  },
  vision: {
    title: "How to use My Vision",
    lines: [
      "Vision statements describe who you want to be, how you want to live, and where you want to end up.",
      "They're grouped by your categories. Tap a category to fold it away.",
      "Tap a statement to see the goals linked to it. The pencil changes the statement and its goals.",
    ],
  },
  keydates: {
    title: "How to use Key Dates",
    lines: [
      "Key Dates are things coming up that you need to get ready for, like a trip, a birthday or a renewal.",
      "Tap a date to see its tasks. Tap Add to today on a task to put it in your day.",
      "Tap + Add a task on a date to give it a task. The pencil changes the date, when reminders start, and the goal it's for.",
      "A date's countdown turns amber on the day its reminders start. From then on, the date shows on Radar with its tasks.",
      "A date can happen once, or every week, month or year. The day after a date that repeats, it moves to the next time, and the tasks you've done come back to do.",
      "You can see your dates on a month calendar. On a phone, tap Calendar at the top. On a wider screen it sits beside the list. Tap a marked day to see what's on it.",
    ],
  },
  mindset: {
    title: "How to use Mindset",
    lines: [
      "Mindset reminders are short thoughts that help you get started when you don't feel like it.",
      "Tap + and choose Mindset reminder to add one. Tap a reminder to change it or delete it.",
      "Drag a reminder by ⠿ to move it up or down. Today's Reminders offers them in the same order when you choose.",
      "On the Today page, tap Today's Reminders. You can choose up to three mindset reminders to keep in view all day.",
    ],
  },
  habits: {
    title: "How to use Habits",
    lines: [
      "Habits are things you want to do more of, or less of. Each one belongs to one of your categories.",
      "↑ is a habit you're building. ↓ is one you're cutting back. Nudge picks one from the words you use. If it picks the wrong one, tap the habit and choose the other.",
      "Tap + and choose Habit to add one. Tap a habit to change its words or category, or to delete it.",
      "Drag a habit by ⠿ to move it up or down within its category.",
      "Tap a category's name to fold it away.",
      "On the Today page, tap Today's Reminders. You can choose up to three habits to focus on that day.",
    ],
  },
  gratitude: {
    title: "How to use Gratitude",
    lines: [
      "Gratitude reminders are the people and things you're thankful for.",
      "Noticing what's already good can lift your mood, and a better mood makes it easier to start the hard things.",
      "Tap + and choose Gratitude reminder to add one. Tap a reminder to change it or delete it.",
      "Drag a reminder by ⠿ to move it up or down. Today's Reminders offers them in the same order when you choose.",
      "On the Today page, tap Today's Reminders. You can choose up to three gratitude reminders to keep in view all day.",
    ],
  },
  temptations: {
    title: "How to use Temptations",
    lines: [
      "Temptations are the things that pull you off track, like your phone, snacks or one more episode.",
      "Each one belongs to one of your categories. Tap a category's name to fold it away.",
      "Tap + and choose Temptation to add one. Tap a temptation to change its words or category, or to delete it.",
      "Drag a temptation by ⠿ to move it up or down within its category.",
      "On the Today page, tap Today's Reminders. You can choose up to three temptations to watch out for that day.",
    ],
  },
  nudges: {
    title: "How to use Nudges",
    lines: [
      "Nudges are the reminders Nudge has sent you, newest first.",
      "Tap a type at the top to see only those nudges.",
      "Tap a nudge's task to open it on the Today page.",
      "Tap Dismiss when you've read a nudge. It moves to ✓ Dismissed, next to All at the top. Tapping Next on Radar's Nudge card moves it there too.",
    ],
  },
};
